import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { SearchHit } from "../../../../creditSearch";
import horizontalSplit from "./utils/horizontalSplit";
import splitArray from "./utils/splitArray";
import { clampColumnWidthPct } from "../../../../utils/maxColumnWidth";
import "../../../../../../Components/Radio/Radio.css";
import { splitGutter } from "./gutterUtils";

const bulletClasses = {
  circle: "circle",
  square: "li-square",
  diamond: "diamond",
  lineVertical: "lineVertical",
  cross: "cross",
};

// Plain Arial glyphs paint the same in the preview and in html2canvas;
// transforms, SVG and fallback-font glyphs do not. `lift` is the glyph's
// ink center above its baseline, so calc(0.5cap - lift) puts that center
// on the middle of the name's capitals for any name font.
const bulletMarks = {
  circle: { glyph: "•", fontSize: "1.4138em", lift: "0.4953em" },
  square: { glyph: "▪", fontSize: "1.5482em", lift: "0.5114em" },
  diamond: { glyph: "♦", fontSize: "0.800em", lift: "0.2164em" },
  lineVertical: { glyph: "I", fontSize: "0.8801em", lift: "0.315em" },
  cross: { glyph: "×", fontSize: "0.8813em", fontWeight: 700, lift: "0.3111em" },
};

// import {BsFillCircleFill, BsDiamondFill, BsFillSquareFill,} from "react-icons/bs";
// import { TbMinusVertical } from "react-icons/tb";
// import { ImCross } from "react-icons/im";
// const getParaIcon = (shape) => {
//   switch (shape) {
//     case "circle":
//       return <BsFillCircleFill />;
//     case "square":
//       return <BsFillSquareFill />;
//     case "diamond":
//       return <BsDiamondFill />;
//     case "lineVertical":
//       return <TbMinusVertical />;
//     case "cross":
//       return <ImCross />;
//     default:
//       return <BsFillCircleFill />;
//   }
// };

const getAlignment = (text_alignment, columns) => {
  const cols = Number(columns) || 1;
  switch (text_alignment) {
    case "left":
      if (cols === 1) {
        return [" text-start "];
      } else if (cols === 2) {
        return [" text-start ", " text-start "];
      } else if (cols === 3)
        return [" text-start ", " text-start ", " text-start "];
      else if (cols === 4) {
        return [" text-start ", " text-start ", " text-start ", " text-start "];
      } else {
        return [" text-start ", " text-start ", " text-start ", " text-start ", " text-start ",];
      }

    case "justify":
      if (cols === 1) {
        return [" text-start "];
      } else if (cols === 2) {
        return [" text-start ", " text-end "];
      } else if (cols === 3)
        return [" text-start ", " text-center ", " text-end "];
      else if (cols === 4) {
        return [" text-start ", " text-start ", " text-end ", " text-end "];
      } else {
        return [" text-start ", " text-start ", " text-center ", " text-end ", " text-end ",];
      }

    case "right":
      if (cols === 1) {
        return [" text-end "];
      } else if (cols === 2) {
        return [" text-end ", " text-end "];
      } else if (cols === 3)
        return [" text-end ", " text-end ", " text-end "];
      else if (cols === 4) {
        return [" text-end ", " text-end ", " text-end ", " text-end "];
      } else {
        return [" text-end ", " text-end ", " text-end ", " text-end ", " text-end ",];
      }

    case "center":
      if (cols === 1) {
        return [" text-center "];
      } else if (cols === 2) {
        return [" text-center ", " text-center "];
      } else if (cols === 3)
        return [" text-center ", " text-center ", " text-center "];
      else if (cols === 4) {
        return [" text-center ", " text-center ", " text-center ", " text-center ",];
      } else {
        return [" text-center ", " text-center ", " text-center ", " text-center ", " text-center ",];
      }
    default:
      // Prefer center when alignment is missing/unknown — text-end made
      // single-column nameonly look right-pinned in export crops.
      if (cols === 1) {
        return [" text-center "];
      } else if (cols === 2) {
        return [" text-end ", " text-start "];
      } else if (cols === 3)
        return [" text-end ", " text-center ", " text-start "];
      else if (cols === 4) {
        return [" text-end ", " text-end ", " text-start ", " text-start "];
      } else {
        return [" text-end ", " text-end ", " text-center ", " text-start ", " text-start ",];
      }
  }
};

const getPadding = (textAlignment, noOfCols, colIdx, gutter) => {
  const cellGutter = splitGutter(gutter);
  let pl = 0;
  let pr = 0;
  if (textAlignment === "gutter") {
    if (noOfCols == 2) {
      if (colIdx == 0) pr = cellGutter;
      if (colIdx == 1) pl = cellGutter;
    }
    if (noOfCols == 3 || noOfCols == 5) {
      if (colIdx > 0) pl = cellGutter;
      if (colIdx < noOfCols - 1) pr = cellGutter;
    }
    if (noOfCols == 4) {
      if (colIdx == 1) pr = cellGutter;
      if (colIdx == 2) pl = cellGutter;
    }
  }
  return {
    paddingLeft: `${pl}px`,
    paddingRight: `${pr}px`,
  };
};

function NameOnly2({items, options, fontStyle, settings, idx,}) {
  const filteredItems = useMemo(() => {
  // Drop empty rows entirely so an empty item never leaves a lone bullet.
  return (items || []).filter((it) => it?.name && String(it.name).trim() !== "");
}, [items]);

const sortedItems = useMemo(() => {
  const copy = [...filteredItems];

  if (options?.column_order === "ascending") {
    copy.sort((a, b) => a.name.localeCompare(b.name));
  } else if (options?.column_order === "descending") {
    copy.sort((a, b) => b.name.localeCompare(a.name));
  }

  return copy;
}, [filteredItems, options?.column_order]);

  const columnCount = Math.max(1, Number(options?.columns) || 1);
  const columnWidth = clampColumnWidthPct(options?.column_width, columnCount);
  const totalWidthPct = Math.min(columnWidth * columnCount, 100);
  const columnFlow = options?.column_flow;
  const balanceColumns = options?.balance_columns;

  const arraySplitData = useMemo(() => {
    const splitOptions = {
      columns: columnCount,
      balance_columns: balanceColumns,
    };
    const raw =
      columnFlow === "vertical"
        ? splitArray(sortedItems, splitOptions)?.ans
        : horizontalSplit(sortedItems, splitOptions)?.ans;
    return Array.from({ length: columnCount }, (_, i) => raw?.[i] || []);
  }, [sortedItems, columnCount, columnFlow, balanceColumns]);

  const textAlignment = getAlignment(options?.text_alignment, columnCount);

  const bulletAlignJSON = { left: "start", right: "end", center: "center" };
  // Paragraph mode only maps left/right/center — fall back so gutter/justify
  // left over from columns mode don't produce justify-undefined.
  const bulletJustify =
    bulletAlignJSON[options.text_alignment] || bulletAlignJSON.center;
  const bulletTextAlign = {
    start: "left",
    center: "center",
    end: "right",
  }[bulletJustify];
  const bulletClass = bulletClasses[options?.bullets] || "";
  const bulletMark = bulletMarks[options?.bullets] || bulletMarks.circle;
  const bulletLineOffset =
    bulletJustify === "center"
      ? "0.6em"
      : bulletJustify === "end"
        ? "1.2em"
        : null;
  const [layoutRevision, setLayoutRevision] = useState(0);
  const [rowLayout, setRowLayout] = useState({
    signature: null,
    ends: [],
  });
  const layoutSignature = JSON.stringify([
    filteredItems.map((item) => item.name),
    options?.bullets,
    columnWidth,
    bulletTextAlign,
    fontStyle?.nameOnlyStyle,
    layoutRevision,
  ]);
  const rowEnds =
    rowLayout.signature === layoutSignature ? rowLayout.ends : null;
  const rowEndSet = useMemo(() => new Set(rowEnds || []), [rowEnds]);

  useLayoutEffect(() => {
    if (!options?.bullets || rowEnds !== null) return;
    const list = document.getElementById("list" + idx);
    if (!list) return;
    const pairs = [...list.querySelectorAll(".name-only-bullet-pair")];
    const ends = [];

    pairs.forEach((pair, itemIndex) => {
      const next = pairs[itemIndex + 1];
      if (!next || next.offsetTop > pair.offsetTop + 1) {
        ends.push(itemIndex);
      }
    });

    setRowLayout({ signature: layoutSignature, ends });
  }, [idx, layoutSignature, options?.bullets, rowEnds]);

  useEffect(() => {
    let cancelled = false;
    document.fonts?.ready?.then?.(() => {
      if (!cancelled) setLayoutRevision((revision) => revision + 1);
    });

    return () => {
      cancelled = true;
    };
  }, [fontStyle?.nameOnlyStyle]);

  useEffect(() => {
    const list = document.getElementById("list" + idx);
    if (!list || typeof ResizeObserver === "undefined") return;
    let previousWidth = list.getBoundingClientRect().width;
    const observer = new ResizeObserver(() => {
      const width = list.getBoundingClientRect().width;
      if (Math.abs(width - previousWidth) <= 0.5) return;
      previousWidth = width;
      setLayoutRevision((revision) => revision + 1);
    });
    observer.observe(list);

    return () => observer.disconnect();
  }, [idx]);

  return options?.bullets ? (
    <div className="w-full overlaytext" style={{ textAlign: "center" }}>
      <div id={"list" + idx} className="FontSIzeScale"
        style={{
          ...fontStyle?.nameOnlyStyle,
          display: "inline-block",
          textAlign: bulletTextAlign,
          verticalAlign: "top",
          whiteSpace: rowEnds === null ? "normal" : "nowrap",
          width: columnWidth + "%",
          boxSizing: "content-box",
          paddingInlineStart: bulletLineOffset || undefined,
          marginInlineEnd: bulletLineOffset
            ? `-${bulletLineOffset}`
            : undefined,
          lineHeight: fontStyle?.nameOnlyStyle?.lineHeight + 0.15,
          textRendering: "geometricPrecision",
          WebkitFontSmoothing: "antialiased",
        }}
      >
        {filteredItems.map((item, i) => (
          <React.Fragment key={i}>
            {/* Keep kerning on: html2canvas paints each word with canvas
                fillText, which always kerns. Unkerned names would render
                narrower than the preview and leave uneven bullet gaps. */}
            <span
              className="name-only-bullet-pair FontSIzeScale"
              style={{
                ...fontStyle?.nameOnlyStyle,
                display: "inline-block",
                whiteSpace: "nowrap",
                lineHeight:
                  fontStyle?.nameOnlyStyle?.lineHeight + 0.15,
                textRendering: "geometricPrecision",
                WebkitFontSmoothing: "antialiased",
              }}
            >
              <span className="name-only-text">
                <SearchHit text={item.name} />
              </span>
              <span
                aria-hidden="true"
                className={`bullet ${bulletClass}${
                  rowEndSet.has(i) ? " rmNewClassbullets" : ""
                }`}
                style={{
                  lineHeight:
                    fontStyle?.nameOnlyStyle?.lineHeight + 0.15,
                }}
              >
                <span
                  className="name-only-bullet-mark"
                  style={{
                    verticalAlign: `calc(0.5cap - ${bulletMark.lift})`,
                  }}
                >
                  <span
                    className="name-only-bullet-glyph"
                    style={{
                      fontSize: bulletMark.fontSize,
                      fontWeight: bulletMark.fontWeight,
                    }}
                  >
                    {bulletMark.glyph}
                  </span>
                </span>
              </span>
            </span>
            {rowEnds === null ? (
              <wbr />
            ) : rowEndSet.has(i) && i < filteredItems.length - 1 ? (
              <br />
            ) : null}
          </React.Fragment>
        ))}
      </div>
    </div>
  ) : (
    <div
      className="overlaytext"
      style={{
        width: `${totalWidthPct}%`,
        margin: "0 auto",
        display: "grid",
        gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
        wordBreak: "break-word",
      }}
    >
      {arraySplitData.map((colItems, index) => (
        <div
          key={index}
          className={`flex flex-col break-words ${textAlignment[index] || ""}`}
          style={{
            minWidth: 0,
            ...getPadding(
              options.text_alignment,
              columnCount,
              index,
              settings.gutter,
            ),
          }}
        >
          {colItems?.map((item, itemIdx) => (
            <p
              key={item?._id || `${item?.name || "name"}-${itemIdx}`}
              className={` FontSIzeScale ${textAlignment[index] || ""}`}
              style={{
                ...fontStyle?.nameOnlyStyle,
                lineHeight: fontStyle?.nameOnlyStyle.lineHeight,
                fontKerning: "none",
                fontFeatureSettings: '"kern" 0',
                textRendering: "geometricPrecision",
                WebkitFontSmoothing: "antialiased",
              }}
            >
              <SearchHit text={item.name} />
            </p>
          ))}
        </div>
      ))}
    </div>
  );
}

const areNameOnlyPropsEqual = (previous, next) =>
  previous.items === next.items &&
  previous.options === next.options &&
  previous.settings === next.settings &&
  previous.idx === next.idx &&
  previous.fontStyle?.nameOnlyStyle === next.fontStyle?.nameOnlyStyle;

export default React.memo(NameOnly2, areNameOnlyPropsEqual);
